import {
  cloneElement,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { lockBodyScroll } from '../../utils/bodyScrollLock';
import { playSound } from '../../utils/utils';
import GlitchPrismScene from './GlitchPrismScene';
import BoardTileLegend from './BoardTileLegend';
import DieSelector from './DieSelector';
import DieMiniature from './DieMiniature';
import BoardTileInspector from './BoardTileInspector';
import InfoButton from './InfoButton';
import CoreboundNavButton from './CoreboundNavButton';
import CoreboundTip from './CoreboundTip';
import SlingshotThrowHelp from './SlingshotThrowHelp';
import { usePopupTransition } from './popupTransition';
import { useCoreboundHelp } from './CoreboundHelp';
import { DEFAULT_DIE, getDie, getDieFaceLabel } from './dice';
import { getChainReactionIndices } from './chainReaction';
import {
  getSlingshotPull,
  getMaxSlingshotPull,
  getSlingshotPreviewTile,
  MIN_SLINGSHOT_POWER,
  normalizeSlingshotLaunch,
} from './slingshot';
import './SlingshotLauncher.css';

const SlingshotLauncher = ({
  doorBoard,
  sector = 1,
  boardCondition = null,
  onLaunch,
  label = 'ROLL',
  aimTitle = 'Your throw',
  aimDescription = null,
  encounter = null,
  coreBoss = null,
  combatControls,
  combatSummary,
  combatSummaryOptions = [],
  attackRecap,
  attackStyle = null,
  dieFaceValues = null,
  relicControls,
  relics = [],
  portalPair = null,
  bountyTagIndex = null,
  chainReactionReady = false,
  tileInfoContext = null,
  selectedDie = DEFAULT_DIE,
  onSelectDie,
  arrival = false,
  onArrivalComplete,
}) => {
  const { isTipVisible, dismissTip } = useCoreboundHelp();
  const [arriving, setArriving] = useState(
    () =>
      arrival &&
      !window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
  );
  const [pull, setPull] = useState({ power: 0, angle: 0, active: false });
  const [fullscreen, setFullscreen] = useState(true);
  const [compactLoadout, setCompactLoadout] = useState(
    () =>
      !!window.matchMedia?.('(max-width: 850px), (max-height: 550px)').matches
  );
  const [loadoutOpen, setLoadoutOpen] = useState(() => !compactLoadout);
  const { close: closeLoadout, popupClass: loadoutPopupClass } =
    usePopupTransition(() => setLoadoutOpen(false), { open: loadoutOpen });
  const [panelView, setPanelView] = useState('dice');
  const recapInPanel = !!(compactLoadout && coreBoss && attackRecap);
  const pullRef = useRef(pull);
  const pointer = useRef(null);
  const keyHold = useRef(null);
  const frame = useRef(null);
  const launched = useRef(false);
  const board = useRef(null);
  const launcher = useRef(null);
  const loadoutButton = useRef(null);
  const loadoutWasOpen = useRef(loadoutOpen);
  useLayoutEffect(() => {
    if (loadoutWasOpen.current && !loadoutOpen)
      loadoutButton.current?.focus({ preventScroll: true });
    loadoutWasOpen.current = loadoutOpen;
  }, [loadoutOpen]);
  const dieControl = useRef(null);
  const tileControls = useRef(null);
  const onLaunchRef = useRef(onLaunch);
  useLayoutEffect(() => {
    const header = launcher.current?.querySelector('.coreboundSlingshotHeader');
    const page =
      launcher.current?.closest('.coreboundPage') ||
      document.querySelector('.coreboundPage');
    if (!header || !page) return undefined;
    const update = () =>
      page.style.setProperty(
        '--corebound-aim-header-height',
        `${header.offsetHeight}px`
      );
    update();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);
  onLaunchRef.current = onLaunch;
  useEffect(() => {
    const media = window.matchMedia?.(
      '(max-width: 850px), (max-height: 550px)'
    );
    if (!media) return undefined;
    const update = () => {
      setCompactLoadout(media.matches);
      if (media.matches) setLoadoutOpen(false);
    };
    if (media.addEventListener) {
      media.addEventListener('change', update);
      return () => media.removeEventListener('change', update);
    }
    media.addListener?.(update);
    return () => media.removeListener?.(update);
  }, []);
  useLayoutEffect(() => {
    if (!fullscreen) return undefined;
    const releaseScroll = lockBodyScroll();
    // The scene stays mounted while the view expands, preserving the die and
    // the portal arrival. Only siblings outside this aiming view become inert.
    const siblings = [];
    let branch = launcher.current;
    while (branch?.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (sibling !== branch && !sibling.matches('.coreboundRunStatus')) {
          siblings.push([sibling, sibling.inert]);
          sibling.inert = true;
        }
      }
      branch = branch.parentElement;
      if (branch === document.body) break;
    }
    const focusFrame = requestAnimationFrame(() => {
      if (!launcher.current?.contains(document.activeElement))
        launcher.current?.focus({ preventScroll: true });
    });
    return () => {
      cancelAnimationFrame(focusFrame);
      siblings.forEach(([sibling, wasInert]) => {
        sibling.inert = wasInert;
      });
      releaseScroll();
    };
  }, [fullscreen]);
  const setPreview = (next) => {
    pullRef.current = next;
    setPull(next);
  };
  const cancelPull = () => {
    pointer.current = null;
    keyHold.current = null;
    cancelAnimationFrame(frame.current);
    frame.current = null;
    setPreview({ power: 0, angle: pullRef.current.angle, active: false });
  };
  const release = () => {
    if (arriving) return;
    pointer.current = null;
    keyHold.current = null;
    cancelAnimationFrame(frame.current);
    frame.current = null;
    const launch = normalizeSlingshotLaunch(pullRef.current);
    if (launch.power < MIN_SLINGSHOT_POWER || launched.current) {
      setPreview({ ...launch, power: 0, active: false });
      return;
    }
    launched.current = true;
    if (!isTipVisible('launch')) dismissTip('bumps');
    dismissTip('launch');
    onLaunchRef.current(launch);
  };
  useEffect(() => {
    const cancel = () => {
      pointer.current = null;
      keyHold.current = null;
      cancelAnimationFrame(frame.current);
      setPull((current) => {
        const next = { ...current, power: 0, active: false };
        pullRef.current = next;
        return next;
      });
    };
    const onVisibility = () => {
      if (document.hidden) cancel();
    };
    window.addEventListener('blur', cancel);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelAnimationFrame(frame.current);
      window.removeEventListener('blur', cancel);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []);

  const startPointer = (event) => {
    if (
      pointer.current ||
      arriving ||
      keyHold.current ||
      launched.current ||
      (event.pointerType === 'mouse' && event.button !== 0)
    )
      return;
    event.preventDefault();
    event.currentTarget.focus({ preventScroll: true });
    event.currentTarget.setPointerCapture(event.pointerId);
    pointer.current = {
      id: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      maxPull: getMaxSlingshotPull(
        board.current?.clientWidth || 600,
        board.current?.clientHeight || Infinity
      ),
    };
    setPreview({ power: 0, angle: 0, active: true });
  };
  const movePointer = (event) => {
    const start = pointer.current;
    if (!start || start.id !== event.pointerId) return;
    const launch = getSlingshotPull(
      event.clientX - start.x,
      event.clientY - start.y,
      start.maxPull
    );
    setPreview({ ...launch, active: true });
  };
  const onKeyDown = (event) => {
    if (arriving) return;
    if (event.key === 'Escape') {
      if (pullRef.current.active) {
        event.preventDefault();
        cancelPull();
      }
      return;
    }
    if (event.key.startsWith('Arrow')) {
      event.preventDefault();
      if (pointer.current) return;
      const current = pullRef.current;
      const next = normalizeSlingshotLaunch({
        power:
          current.power +
          (event.key === 'ArrowUp'
            ? 0.05
            : event.key === 'ArrowDown'
            ? -0.05
            : 0),
        angle:
          current.angle +
          (event.key === 'ArrowLeft'
            ? -Math.PI / 36
            : event.key === 'ArrowRight'
            ? Math.PI / 36
            : 0),
      });
      setPreview({ ...next, active: true });
      return;
    }
    if (event.key !== ' ' && event.key !== 'Enter') return;
    event.preventDefault();
    if (event.repeat || keyHold.current || pointer.current || launched.current)
      return;
    keyHold.current = {
      key: event.key,
      start: performance.now(),
      power: pullRef.current.power,
    };
    const tick = (now) => {
      if (!keyHold.current) return;
      setPreview({
        ...pullRef.current,
        active: true,
        power: Math.min(
          1,
          keyHold.current.power + (now - keyHold.current.start) / 1600
        ),
      });
      frame.current = requestAnimationFrame(tick);
    };
    frame.current = requestAnimationFrame(tick);
  };

  const ready = pull.power >= MIN_SLINGSHOT_POWER;
  const die = getDie(selectedDie);
  const degrees = Math.round((Math.abs(pull.angle) * 180) / Math.PI);
  const direction =
    degrees < 3
      ? 'STRAIGHT'
      : `${degrees}° ${pull.angle < 0 ? 'LEFT' : 'RIGHT'}`;
  const preview = pull;
  const showLaunchTip = isTipVisible('launch') && !arriving && !pull.active;
  const showHelp =
    !arriving &&
    !pull.active &&
    !launched.current &&
    !(compactLoadout && loadoutOpen);
  const targetTile = getSlingshotPreviewTile(preview, boardCondition);
  const dieSelector = (
    <DieSelector
      selectedDie={die.id}
      attackStyle={attackStyle}
      disabled={
        arriving ||
        !!pointer.current ||
        !!keyHold.current ||
        launched.current ||
        !onSelectDie
      }
      onSelect={(id) => {
        if (arriving || launched.current || pointer.current || keyHold.current)
          return;
        cancelPull();
        onSelectDie?.(id);
        playSound('/sounds/menu-select.mp3');
      }}
    />
  );
  const onDockToggle = (event) => {
    const dock = event.currentTarget;
    if (!dock.open) return;
    if (pullRef.current.active) cancelPull();
    if (compactLoadout)
      launcher.current
        ?.querySelectorAll('.coreboundSlingshotCanvasDock[open]')
        .forEach((other) => {
          if (other !== dock) other.open = false;
        });
  };
  const onDockKeyDown = (event) => {
    if (event.defaultPrevented || event.key !== 'Escape') return;
    if (event.currentTarget.open) {
      event.preventDefault();
      event.currentTarget.open = false;
      event.currentTarget
        .querySelector('summary')
        ?.focus({ preventScroll: true });
    }
  };

  const recapProps = {
    // Reserve the recap during camera return instead of inserting it later.
    style: { visibility: arriving ? 'hidden' : undefined },
    'aria-hidden': arriving || undefined,
    inert: arriving ? '' : undefined,
    onPointerDownCapture: cancelPull,
    onFocusCapture: cancelPull,
  };
  const recap = !attackRecap ? null : isValidElement(attackRecap) ? (
    cloneElement(attackRecap, recapProps)
  ) : (
    <div {...recapProps}>{attackRecap}</div>
  );

  return (
    <div
      ref={launcher}
      className={`coreboundSlingshotLauncher ${
        pull.active ? 'is-pulling' : ''
      } ${arriving ? 'is-arriving' : ''} ${fullscreen ? 'is-fullscreen' : ''} ${
        loadoutOpen ? 'is-loadout-open' : ''
      } ${coreBoss ? 'has-core-boss' : ''}`}
      role={fullscreen ? 'dialog' : undefined}
      aria-modal={fullscreen ? true : undefined}
      aria-label={fullscreen ? `${aimTitle}: aim your die` : undefined}
      tabIndex={fullscreen ? -1 : undefined}
      aria-busy={arriving}
      onKeyDownCapture={(event) => {
        const disclosure = event.target.closest(
          '.coreboundConditionInline[open], .coreboundBoardTileLegend[open], .coreboundAttackBreakdown[open]'
        );
        if (event.key === 'Escape' && disclosure) {
          event.preventDefault();
          event.stopPropagation();
          disclosure.open = false;
          disclosure.querySelector('summary')?.focus();
        }
      }}
      onKeyDown={(event) => {
        if (!fullscreen || event.defaultPrevented) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          cancelPull();
          if (loadoutOpen) {
            closeLoadout();
          } else setFullscreen(false);
        }
        if (event.key === 'Tab') {
          const controls = Array.from(
            launcher.current.querySelectorAll(
              'button:not(:disabled), summary, input:not(:disabled), select:not(:disabled), [tabindex="0"]'
            )
          ).filter(
            (element) =>
              element.tabIndex >= 0 &&
              element.getClientRects().length &&
              !element.closest('[inert]')
          );
          const first = controls[0];
          const last = controls[controls.length - 1];
          if (
            event.shiftKey &&
            (document.activeElement === first ||
              document.activeElement === launcher.current)
          ) {
            event.preventDefault();
            last?.focus();
          } else if (
            !event.shiftKey &&
            (document.activeElement === last ||
              document.activeElement === launcher.current)
          ) {
            event.preventDefault();
            first?.focus();
          }
        }
      }}
    >
      <header className="coreboundSlingshotHeader">
        <div className="coreboundSlingshotHeading">
          <strong>{aimTitle}</strong>
          {aimDescription && <small>{aimDescription}</small>}
        </div>
        <div className="coreboundSlingshotViewActions">
          <div className="coreboundBoardHelpActions">
            <BoardTileLegend onOpen={cancelPull} />
            <InfoButton onOpen={cancelPull} relics={relics} />
          </div>

          <CoreboundNavButton
            className={`coreboundOverviewButton ${
              fullscreen ? '' : 'is-aim-primary'
            }`}
            onClick={() => {
              cancelPull();
              if (compactLoadout) setLoadoutOpen(false);
              setFullscreen((expanded) => !expanded);
            }}
            aria-label={
              fullscreen ? 'Exit full-screen aiming' : 'Open full-screen aiming'
            }
          >
            {!fullscreen && (
              <svg
                className="coreboundFullscreenIcon"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
                focusable="false"
              >
                <path d="M9 3H3v6M15 3h6v6M21 15v6h-6M9 21H3v-6" />
              </svg>
            )}
            {fullscreen ? (coreBoss ? 'View' : 'Overview') : 'Aim full screen'}
          </CoreboundNavButton>
        </div>
      </header>
      <div className="coreboundSlingshotWorkspace">
        <div
          className="coreboundSlingshotDeck"
          inert={fullscreen && loadoutOpen && compactLoadout ? '' : undefined}
        >
          <div className="coreboundSlingshotBoard" ref={board}>
            <div className="coreboundSlingshotViewport">
              <button
                ref={loadoutButton}
                className="coreboundDiceNavigation"
                type="button"
                aria-expanded={loadoutOpen}
                aria-controls="coreboundSlingshotLoadout"
                aria-label={`${
                  recapInPanel ? 'Open dice and last attack panel' : 'Swap dice'
                }. Current die: ${die.name}.`}
                onClick={() => {
                  cancelPull();
                  if (loadoutOpen) closeLoadout();
                  else setLoadoutOpen(true);
                  if (!loadoutOpen && compactLoadout)
                    requestAnimationFrame(() =>
                      launcher.current
                        ?.querySelector(
                          '.coreboundSlingshotLoadoutHeader button'
                        )
                        ?.focus({ preventScroll: true })
                    );
                }}
              >
                <DieMiniature
                  dieId={die.id}
                  faceValues={die.id === 'chaos' ? undefined : dieFaceValues}
                  rotating
                  className="coreboundDiceNavigationIcon"
                />
                <svg
                  className="coreboundDiceSwapSymbol"
                  aria-hidden="true"
                  viewBox="0 0 24 24"
                >
                  <path d="M4 8h15m-4-4 4 4-4 4M20 16H5m4-4-4 4 4 4" />
                </svg>
              </button>
              {showHelp &&
                (showLaunchTip ? (
                  <CoreboundTip
                    id="launch"
                    title="Pull back, then release"
                    floating
                  >
                    <p>
                      Drag the glowing die handle down for power. Pull left to
                      aim right, or right to aim left, then release.
                    </p>
                    <p>
                      Keyboard: focus the handle, use Left/Right to aim, then
                      hold and release Space. Power changes distance, not the
                      die's number. Use Dice to change its faces.
                    </p>
                  </CoreboundTip>
                ) : isTipVisible('bumps') ? (
                  <CoreboundTip
                    id="bumps"
                    title="Nudge the moving die"
                    floating
                  >
                    <p>
                      After you throw, tap the board toward a tile while the die
                      moves, or press Space or Bump Board for a forward nudge.
                      Each throw has three bumps. A bump can change the landing
                      tile and top face; the result locks when the die stops.
                    </p>
                  </CoreboundTip>
                ) : coreBoss ? (
                  <CoreboundTip
                    id="core"
                    title="Facing the Glitch Core"
                    floating
                  >
                    <p>
                      Choose an attack style before throwing. Your final roll
                      damages the Core. Check its next attack beside the style
                      buttons to see how much health you might lose.
                    </p>
                    <p>
                      If the Core survives, use a relic if needed, then tap
                      Start the Core's turn. Shields and relics can protect you
                      from its hit. Keep chipping away at its health.
                    </p>
                  </CoreboundTip>
                ) : portalPair?.armed ? (
                  <CoreboundTip
                    id="portalPair"
                    title="Portal pair detected"
                    floating
                  >
                    <p>
                      Cross either ring while airborne to emerge from the other
                      with your momentum intact. This pair works once per throw,
                      and your landing tile still resolves normally.
                    </p>
                  </CoreboundTip>
                ) : null)}
              <GlitchPrismScene
                sector={sector}
                phase="setup"
                arrival={arriving}
                onArrivalComplete={() => {
                  setArriving(false);
                  onArrivalComplete?.();
                }}
                dieType={die.id}
                doorBoard={doorBoard}
                boardCondition={boardCondition}
                charge={pull.power}
                attackStyle={attackStyle}
                dieFaceValues={dieFaceValues}
                slingshotPreview={preview}
                setupDieControlRef={dieControl}
                setupTileControlRef={tileControls}
                preserveSetupFraming={!fullscreen}
                portalPair={portalPair}
                bountyTagIndex={bountyTagIndex}
                chainReactionReady={
                  chainReactionReady &&
                  doorBoard?.[targetTile]?.effect?.type !== 'powerUp'
                }
                encounter={encounter}
                coreBoss={coreBoss}
              />
              <button
                ref={dieControl}
                type="button"
                disabled={arriving}
                className={`coreboundSlingshotGrip ${ready ? 'is-ready' : ''} ${
                  showLaunchTip && showHelp ? 'is-learning' : ''
                }`}
                aria-label={`Pull the die down and release to ${label.toLowerCase()}. Power ${Math.round(
                  pull.power * 100
                )} percent, direction ${direction.toLowerCase()}.`}
                aria-describedby="coreboundSlingshotInstructions"
                onPointerDown={startPointer}
                onPointerMove={movePointer}
                onPointerUp={(event) => {
                  if (pointer.current?.id === event.pointerId) {
                    movePointer(event);
                    release();
                  }
                }}
                onPointerCancel={cancelPull}
                onLostPointerCapture={() => {
                  if (pointer.current) cancelPull();
                }}
                onKeyDown={onKeyDown}
                onKeyUp={(event) => {
                  if (keyHold.current?.key === event.key) {
                    event.preventDefault();
                    release();
                  }
                }}
                onBlur={() => {
                  if (keyHold.current) cancelPull();
                }}
                onClick={(event) => {
                  if (
                    event.detail === 0 &&
                    !arriving &&
                    !pointer.current &&
                    !keyHold.current &&
                    !launched.current
                  ) {
                    if (!ready)
                      setPreview({
                        ...pullRef.current,
                        power: 0.6,
                        active: true,
                      });
                    release();
                  }
                }}
                onContextMenu={(event) => event.preventDefault()}
              >
                <svg viewBox="0 0 36 36" aria-hidden="true">
                  <path d="M18 3 L32 11 L32 26 L18 34 L4 26 L4 11 Z M4 11 L18 19 L32 11 M18 19 L18 34" />
                  <circle cx="18" cy="11" r="2" />
                  <circle cx="11" cy="23" r="2" />
                  <circle cx="25" cy="23" r="2" />
                </svg>
                {showLaunchTip && showHelp && (
                  <span className="coreboundSlingshotGhost" aria-hidden="true">
                    <svg viewBox="0 0 36 36">
                      <path d="M18 3 L32 11 L32 26 L18 34 L4 26 L4 11 Z M4 11 L18 19 L32 11 M18 19 L18 34" />
                      <circle cx="18" cy="11" r="2" />
                      <circle cx="11" cy="23" r="2" />
                      <circle cx="25" cy="23" r="2" />
                    </svg>
                    <small>Pull ↓ · Release</small>
                  </span>
                )}
              </button>
              <BoardTileInspector
                ref={tileControls}
                board={doorBoard || []}
                disabled={
                  arriving ||
                  pull.active ||
                  launched.current ||
                  (fullscreen && loadoutOpen && compactLoadout)
                }
                context={tileInfoContext}
                boardCondition={boardCondition}
                portalPair={portalPair}
                bountyTagIndex={bountyTagIndex}
                chainReactionReady={chainReactionReady}
              />
              {relicControls && (
                <details
                  className="coreboundSlingshotCanvasDock coreboundSlingshotRelicDock"
                  aria-label="Relic abilities"
                  open={fullscreen && !compactLoadout}
                  inert={arriving ? '' : undefined}
                  onToggle={onDockToggle}
                  onKeyDown={onDockKeyDown}
                >
                  <summary>
                    <strong>Relic abilities</strong>
                    <span
                      className="coreboundSlingshotDockChevron"
                      aria-hidden="true"
                    >
                      ⌃
                    </span>
                  </summary>
                  <div className="coreboundSlingshotDockPanel">
                    {relicControls}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
        {fullscreen && loadoutOpen && (
          <button
            type="button"
            className="coreboundSlingshotLoadoutBackdrop"
            aria-label="Return to aiming"
            tabIndex={-1}
            onClick={() => {
              closeLoadout();
            }}
          />
        )}
        <aside
          id="coreboundSlingshotLoadout"
          className={`coreboundSlingshotLoadout ${
            loadoutOpen ? loadoutPopupClass : ''
          }`}
          hidden={fullscreen && !loadoutOpen}
          aria-label={recapInPanel ? 'Dice and last attack' : 'Dice selection'}
          inert={arriving ? '' : undefined}
        >
          {fullscreen && (
            <div className="coreboundSlingshotLoadoutHeader">
              <strong>{recapInPanel ? 'DICE & LAST ATTACK' : 'DICE'}</strong>
              <button
                type="button"
                onClick={() => {
                  closeLoadout();
                }}
              >
                Back to aim ×
              </button>
            </div>
          )}
          {recapInPanel && (
            <div
              className="coreboundSlingshotPanelChoices"
              role="group"
              aria-label="Side panel view"
            >
              <button
                type="button"
                aria-pressed={panelView === 'dice'}
                onClick={() => setPanelView('dice')}
              >
                Dice
              </button>
              <button
                type="button"
                aria-pressed={panelView === 'recap'}
                onClick={() => setPanelView('recap')}
              >
                Last attack
              </button>
            </div>
          )}
          {recapInPanel && panelView === 'recap' ? (
            recap
          ) : (
            <>
              {dieSelector}
              <p
                className="coreboundDieLoadout"
                style={{ '--die-color': die.color }}
                aria-live="polite"
              >
                <strong>
                  {die.name} · {die.label}
                </strong>
                <span>
                  {die.id === 'chaos'
                    ? '? · ? · ? · ? · ? · ? — revealed on board contact'
                    : dieFaceValues?.join(' · ') || getDieFaceLabel(die.id)}
                </span>
                <small>Board and relic effects apply after landing.</small>
              </p>
            </>
          )}
        </aside>
      </div>
      <div className="coreboundSlingshotHud">
        {!combatControls && !recapInPanel && recap}
        {combatControls && (
          <section
            className="coreboundSlingshotCombatBar"
            aria-label="Choose your attack style before throwing"
            inert={
              arriving || (fullscreen && loadoutOpen && compactLoadout)
                ? ''
                : undefined
            }
            onPointerDownCapture={() => {
              if (pullRef.current.active) cancelPull();
            }}
          >
            {combatControls}
            <p
              className="coreboundSlingshotCombatSelection"
              role="status"
              aria-live="polite"
            >
              <span className="coreboundCombatSelectionCurrent">
                {combatSummary}
              </span>
              {combatSummaryOptions.map((summary, index) => (
                <span
                  key={index}
                  className="coreboundCombatSelectionReserve"
                  aria-hidden="true"
                >
                  {summary}
                </span>
              ))}
            </p>
            {!recapInPanel && recap}
          </section>
        )}
        <div className="coreboundSlingshotHudTop">
          <div className="coreboundSlingshotReadout">
            <span>THROW POWER</span>
            <strong>{Math.round(pull.power * 100)}%</strong>
          </div>
          <div
            className="coreboundSlingshotMeter"
            role="progressbar"
            aria-label="Throw power"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pull.power * 100)}
          >
            <span style={{ width: `${pull.power * 100}%` }} />
            <i style={{ left: `${MIN_SLINGSHOT_POWER * 100}%` }} />
          </div>
          <div className="coreboundSlingshotTarget">
            <SlingshotThrowHelp onOpen={cancelPull} />
          </div>
        </div>
        {chainReactionReady && (
          <p className="coreboundChainAimHint" role="status">
            {pull.active
              ? doorBoard?.[targetTile]?.effect?.type === 'powerUp'
                ? 'CHAIN ARMED · Waiting for a non-Power Up landing'
                : `CHAIN PREVIEW · ${
                    getChainReactionIndices(targetTile).length
                  } highlighted tiles`
              : 'CHAIN REACTION · Pull to preview tiles'}
            <small>
              First-bounce estimate. Later bounces and bumps can change the
              landing.
            </small>
          </p>
        )}
      </div>
    </div>
  );
};

export default SlingshotLauncher;
